import type {
  ColDef,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import type { IntlShape } from 'react-intl'
import { styled } from 'styled-components'

import type { AttributeDisplayPropertiesDto } from '@b-stock/fe-core/bstock-next/components'
import {
  CellReturnType,
  renderUnknownCell,
} from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedItemWithGroup } from '@b-stock/fe-core/bstock-next/queries'

export type UnknownCellRendererParams = {
  attributeKey: string
  currency?: string
  display: AttributeDisplayPropertiesDto | undefined
  intl: IntlShape
}

export const Body = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const UnknownCellRenderer = (
  params: ICellRendererParams<OrchestratedItemWithGroup> &
    UnknownCellRendererParams
) => {
  const { attributeKey, currency, data, display, intl } = params
  if (!data) return null

  const content = renderUnknownCell({
    attributeKey,
    currency,
    display,
    intl,
    item: data,
    value: params.value,
  })

  return <Body data-type={getDataType(params.value)}>{content}</Body>
}

export function buildUnknownCellValueFormatter({
  attributeKey,
  currency,
  display,
  intl,
}: UnknownCellRendererParams): NonNullable<ColDef['valueFormatter']> {
  return (params: ValueFormatterParams<OrchestratedItemWithGroup>) => {
    if (!params.data) return ''

    return renderUnknownCell({
      attributeKey,
      currency,
      display,
      intl,
      item: params.data,
      returnType: CellReturnType.String,
      value: params.value,
    })
  }
}

export function buildUnknownCellTooltipValueGetter({
  attributeKey,
  currency,
  display,
  intl,
}: UnknownCellRendererParams): NonNullable<ColDef['tooltipValueGetter']> {
  return (params: ITooltipParams<OrchestratedItemWithGroup>) => {
    if (!params.data) return ''

    return renderUnknownCell({
      attributeKey,
      currency,
      display,
      intl,
      item: params.data,
      returnType: CellReturnType.String,
      value: params.value,
    })
  }
}

const getDataType = (value: unknown): string => {
  const rawType = typeof value
  if (rawType === 'object') return Array.isArray(value) ? 'array' : 'object'
  return rawType
}
